// Privacy Policy content - CCPA compliant
export const PRIVACY_CONTENT = {
  hero: {
    title: 'Privacy Policy',
    subtitle: 'Your privacy matters to us',
    lastUpdated: 'Last Updated: October 22, 2025',
    effectiveDate: 'Effective Date: October 22, 2025'
  },

  introduction: {
    title: 'Introduction',
    content: [
      'HammerSawPaint ("HSP," "we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our platform and services.',
      'We operate as a coordination platform connecting customers with independent home service professionals. To provide our services, we must share certain information with trusted third parties (service providers) solely for the purpose of booking and performing services.',
      'This Privacy Policy complies with the California Consumer Privacy Act (CCPA), California Privacy Rights Act (CPRA), and other applicable privacy laws.'
    ]
  },

  sections: [
    {
      title: '1. Information We Collect',
      subsections: [
        {
          subtitle: '1.1 Information You Provide',
          content: [
            'We collect information you voluntarily provide when using our services:',
            '• Account Information: Name, email address, phone number, password',
            '• Property Information: Service address, property type, lot size, access instructions',
            '• Billing Information: Credit card details, billing address, payment history',
            '• Service Preferences: Scheduling preferences, service frequency, special instructions',
            '• Communications: Messages, feedback, support requests, reviews',
            '• Profile Information: Account settings, notification preferences'
          ]
        },
        {
          subtitle: '1.2 Information Collected Automatically',
          content: [
            'When you use our platform, we automatically collect:',
            '• Device Information: IP address, browser type, device type, operating system',
            '• Usage Data: Pages viewed, features used, time spent, click patterns',
            '• Location Data: General location derived from IP address (not precise GPS)',
            '• Cookies and Tracking: See Section 8 for details',
            '• Service History: Completed services, service dates, service providers used'
          ]
        },
        {
          subtitle: '1.3 Information from Third Parties',
          content: [
            'We may receive information from:',
            '• Service Providers: Feedback about service completion, issues, or concerns',
            '• Payment Processors: Transaction verification, fraud prevention data',
            '• Background Check Services: Verification data for service provider vetting',
            '• Public Records: Property information, business licenses, insurance verification'
          ]
        }
      ]
    },
    {
      title: '2. How We Use Your Information',
      subsections: [
        {
          subtitle: '2.1 Primary Service Purposes',
          content: [
            'We use your information to:',
            '• Coordinate and schedule home services with independent contractors',
            '• Process payments and manage your subscription',
            '• Communicate with you about services, schedules, and updates',
            '• Share necessary information with service providers to complete work',
            '• Provide customer support and address concerns',
            '• Maintain and improve our platform functionality'
          ]
        },
        {
          subtitle: '2.2 Service Provider Coordination',
          content: [
            'IMPORTANT: To book and perform services, we share your information with trusted independent service providers, including:',
            '• Your name and contact information',
            '• Service address and property details',
            '• Access instructions and special requirements',
            '• Service history and preferences',
            '• Any specific instructions or concerns you\'ve communicated',
            'Service providers use this information ONLY to perform the services you\'ve requested. They are contractually prohibited from using your information for any other purpose or sharing it with others.'
          ]
        },
        {
          subtitle: '2.3 Business Operations',
          content: [
            '• Quality assurance and service monitoring',
            '• Fraud prevention and security',
            '• Legal compliance and protecting our rights',
            '• Business analytics and platform improvements',
            '• Marketing and promotional communications (with your consent)',
            '• Research and development'
          ]
        }
      ]
    },
    {
      title: '3. How We Share Your Information',
      subsections: [
        {
          subtitle: '3.1 Service Providers (Independent Contractors)',
          content: [
            'We share your information with independent service professionals to book and perform services. This includes:',
            '• Contact and property information for service delivery',
            '• Service history to ensure continuity and quality',
            '• Special instructions, access codes, or safety information',
            '• Feedback you\'ve provided about previous services',
            'Each service provider:',
            '• Is vetted and background-checked',
            '• Signs confidentiality agreements',
            '• May only use your information to perform services',
            '• Must protect your information as required by law',
            '• Is prohibited from sharing your information with others'
          ]
        },
        {
          subtitle: '3.2 Business Service Providers',
          content: [
            'We share information with trusted third-party companies that help us operate, including:',
            '• Payment Processors: To process transactions securely (Stripe, etc.)',
            '• Cloud Hosting: To store data securely (AWS, Google Cloud, etc.)',
            '• Communication Tools: To send emails and notifications (SendGrid, Twilio, etc.)',
            '• Analytics Providers: To understand platform usage (Google Analytics, etc.)',
            '• Background Check Services: To vet service providers',
            'These providers are contractually required to protect your information and use it only for specified purposes.'
          ]
        },
        {
          subtitle: '3.3 Legal Requirements',
          content: [
            'We may disclose information when required by law:',
            '• To comply with legal obligations, court orders, or subpoenas',
            '• To protect our rights, property, or safety',
            '• To prevent fraud or illegal activity',
            '• In connection with legal proceedings',
            '• To enforce our Terms of Service'
          ]
        },
        {
          subtitle: '3.4 Business Transfers',
          content: [
            'If HSP is involved in a merger, acquisition, or sale of assets, your information may be transferred. You will be notified of any such change and your rights regarding your information.'
          ]
        },
        {
          subtitle: '3.5 With Your Consent',
          content: [
            'We may share information for other purposes with your explicit consent.'
          ]
        }
      ]
    },
    {
      title: '4. California Privacy Rights (CCPA/CPRA)',
      subsections: [
        {
          subtitle: '4.1 Your Rights',
          content: [
            'California residents have the following rights:',
            '• Right to Know: Request disclosure of personal information collected, used, and shared',
            '• Right to Delete: Request deletion of your personal information',
            '• Right to Correct: Request correction of inaccurate information',
            '• Right to Opt-Out: Opt-out of sale or sharing of personal information (Note: We do not sell personal information)',
            '• Right to Limit Use: Limit use of sensitive personal information',
            '• Right to Non-Discrimination: Not receive discriminatory treatment for exercising rights'
          ]
        },
        {
          subtitle: '4.2 Categories of Information Collected (CCPA Disclosure)',
          content: [
            '• Identifiers: Name, email, phone, address, IP address',
            '• Commercial Information: Purchase history, service records',
            '• Internet Activity: Website usage, browsing history on our platform',
            '• Geolocation Data: General location (not precise GPS)',
            '• Professional Information: If provided for business accounts',
            'We do NOT collect: Social security numbers, driver\'s license numbers, financial account numbers (processed by payment processor), precise geolocation, or biometric data.'
          ]
        },
        {
          subtitle: '4.3 Business Purposes for Collection',
          content: [
            '• Providing and improving our services',
            '• Coordinating with service providers',
            '• Processing transactions',
            '• Customer support',
            '• Security and fraud prevention',
            '• Legal compliance',
            '• Business analytics'
          ]
        },
        {
          subtitle: '4.4 Data Sharing Categories (Last 12 Months)',
          content: [
            '• Service Providers (Independent Contractors): Contact info, property details, service history',
            '• Payment Processors: Billing and transaction data',
            '• Cloud Services: All data categories for hosting',
            '• Analytics Providers: Usage and device data',
            '• Background Check Services: Service provider verification data',
            'We do NOT sell personal information for monetary or other valuable consideration.'
          ]
        },
        {
          subtitle: '4.5 How to Exercise Your Rights',
          content: [
            'To exercise your California privacy rights:',
            '• Email: support@hsppro.com with "Privacy Request" in subject',
            '• Phone: (888) 504-6944',
            '• Customer Portal: Use privacy settings',
            'We will verify your identity and respond within 45 days (may extend to 90 days if complex). You may designate an authorized agent to make requests on your behalf.'
          ]
        }
      ]
    },
    {
      title: '5. Data Security',
      subsections: [
        {
          subtitle: '5.1 Security Measures',
          content: [
            'We implement industry-standard security measures:',
            '• Encryption: SSL/TLS encryption for data in transit',
            '• Secure Storage: Encrypted databases and secure cloud hosting',
            '• Access Controls: Limited employee access on need-to-know basis',
            '• Authentication: Secure login and password requirements',
            '• Regular Audits: Security assessments and vulnerability testing',
            '• Payment Security: PCI-DSS compliant payment processing'
          ]
        },
        {
          subtitle: '5.2 No Absolute Security',
          content: [
            'While we take reasonable precautions, no system is completely secure. We cannot guarantee absolute security of your information. You are responsible for maintaining the confidentiality of your account credentials.'
          ]
        },
        {
          subtitle: '5.3 Breach Notification',
          content: [
            'In the event of a data breach affecting your personal information, we will notify you as required by California law (California Civil Code Section 1798.82).'
          ]
        }
      ]
    },
    {
      title: '6. Data Retention',
      subsections: [
        {
          subtitle: '6.1 Retention Periods',
          content: [
            'We retain information for as long as necessary to provide services and comply with legal obligations:',
            '• Active Account Data: Retained while your account is active',
            '• Transaction Records: 7 years for tax and legal compliance',
            '• Service History: 5 years for quality assurance and liability purposes',
            '• Marketing Data: Until you opt-out or request deletion',
            '• Legal Holds: As required by law or litigation'
          ]
        },
        {
          subtitle: '6.2 Deletion Requests',
          content: [
            'Upon verified deletion request, we will delete or anonymize your information, except where retention is required by law or legitimate business purposes (e.g., pending disputes, warranty claims, fraud prevention).'
          ]
        }
      ]
    },
    {
      title: '7. Children\'s Privacy',
      content: [
        'Our services are not intended for children under 18. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately and we will delete it.'
      ]
    },
    {
      title: '8. Cookies and Tracking Technologies',
      subsections: [
        {
          subtitle: '8.1 Types of Cookies',
          content: [
            '• Essential Cookies: Required for platform functionality',
            '• Performance Cookies: Help us understand usage and improve services',
            '• Functional Cookies: Remember your preferences',
            '• Analytics Cookies: Google Analytics and similar tools',
            'We do not use advertising cookies or sell data to advertisers.'
          ]
        },
        {
          subtitle: '8.2 Managing Cookies',
          content: [
            'You can control cookies through your browser settings. Disabling cookies may affect platform functionality. You can opt-out of Google Analytics at https://tools.google.com/dlpage/gaoptout'
          ]
        }
      ]
    },
    {
      title: '9. Marketing Communications',
      subsections: [
        {
          subtitle: '9.1 Opt-In',
          content: [
            'We send marketing emails only with your consent. Service-related communications (scheduling, billing, important updates) are not marketing and cannot be opted out of while you have an active account.'
          ]
        },
        {
          subtitle: '9.2 Opt-Out',
          content: [
            'To opt-out of marketing:',
            '• Click "unsubscribe" in any marketing email',
            '• Update preferences in your customer portal',
            '• Email support@hsppro.com with "Unsubscribe"',
            '• Call (888) 504-6944'
          ]
        }
      ]
    },
    {
      title: '10. Third-Party Links',
      content: [
        'Our platform may contain links to third-party websites (e.g., payment processors, insurance providers). We are not responsible for their privacy practices. Please review their privacy policies.'
      ]
    },
    {
      title: '11. Changes to Privacy Policy',
      content: [
        'We may update this Privacy Policy periodically. Changes will be posted with a new "Last Updated" date. Material changes will be communicated via email or platform notification. Continued use after changes constitutes acceptance.'
      ]
    },
    {
      title: '12. Contact Us',
      content: [
        'For privacy questions, concerns, or to exercise your rights:',
        'HammerSawPaint Privacy Team',
        'Email: support@hsppro.com',
        'Phone: (888) 504-6944',
        'Website: www.hsppro.com',
        'Mailing Address: [Your business address]',
        '',
        'California Residents - Consumer Complaint Information:',
        'California Department of Consumer Affairs',
        'Complaint Assistance Unit',
        '1625 North Market Blvd., Suite N 112',
        'Sacramento, CA 95834',
        'Phone: (800) 952-5210',
        'Website: www.dca.ca.gov'
      ]
    }
  ],

  footer: {
    acknowledgment: 'By using HammerSawPaint\'s services, you acknowledge that you have read and understood this Privacy Policy.',
    commitment: 'We are committed to protecting your privacy and handling your information responsibly.'
  }
};
